import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 基础学科 - 横屏「SVG 动态图解」风格
 *
 * 核心亮点：
 * - SVG 正弦波 / 余弦波实时绘制动画
 * - 动态坐标系 + 网格线
 * - 关键参数高亮浮标
 * - 粒子沿曲线运动
 * - 公式与图形联动
 *
 * 尺寸：1920x1080 横屏
 */

interface WaveParam {
  label: string;
  symbol: string;
  value: string;
  color: string;
}

interface AcademicAnimatedDiagramProps {
  subject: string;
  topic: string;
  subtitle: string;
  waveParams: WaveParam[];
  formula: string;
  explanation: string[];
  accentColor?: string;
}

const DEFAULT_PROPS: AcademicAnimatedDiagramProps = {
  subject: '高中物理',
  topic: '简谐运动与正弦波',
  subtitle: 'Simple Harmonic Motion & Sine Wave',
  waveParams: [
    { label: '振幅', symbol: 'A', value: '2 cm', color: '#f472b6' },
    { label: '周期', symbol: 'T', value: '2π s', color: '#818cf8' },
    { label: '频率', symbol: 'f', value: '1/2π Hz', color: '#34d399' },
    { label: '波长', symbol: 'λ', value: '4 m', color: '#fbbf24' },
  ],
  formula: 'y = A sin(ωt + φ)',
  explanation: [
    '振幅 A 决定波的最大位移',
    '角频率 ω = 2π/T 决定振动快慢',
    '初相 φ 决定波的起始位置',
  ],
  accentColor: '#818cf8',
};

const AnimatedWave: React.FC<{
  frame: number;
  width: number;
  height: number;
  amplitude: number;
  color: string;
  speed: number;
  phase: number;
  drawProgress: number;
}> = ({ frame, width, height, amplitude, color, speed, phase, drawProgress }) => {
  const points: string[] = [];
  const totalPoints = Math.floor(width * drawProgress);

  for (let x = 0; x <= totalPoints; x += 2) {
    const normalX = (x / width) * 4 * Math.PI;
    const y = height / 2 - amplitude * Math.sin(normalX - frame * speed * 0.05 + phase);
    points.push(`${x},${y}`);
  }

  return (
    <polyline
      points={points.join(' ')}
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
    />
  );
};

const MovingDot: React.FC<{
  frame: number;
  width: number;
  height: number;
  amplitude: number;
  speed: number;
  color: string;
}> = ({ frame, width, height, amplitude, speed, color }) => {
  const x = (frame * 2) % width;
  const normalX = (x / width) * 4 * Math.PI;
  const y = height / 2 - amplitude * Math.sin(normalX - frame * speed * 0.05);

  return (
    <>
      <circle cx={x} cy={y} r="8" fill={color} opacity="0.9" />
      <circle cx={x} cy={y} r="20" fill={color} opacity="0.15" />
      {/* Vertical projection line */}
      <line x1={x} y1={y} x2={x} y2={height / 2} stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
      {/* Horizontal projection line */}
      <line x1={0} y1={y} x2={x} y2={y} stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.3" />
    </>
  );
};

export const AcademicAnimatedDiagram: React.FC<Partial<AcademicAnimatedDiagramProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = p.accentColor!;

  const waveW = 900;
  const waveH = 360;
  const amplitude = 120;

  // Phases
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const gridDraw = interpolate(frame, [20, 60], [0, 1], { extrapolateRight: 'clamp' });
  const waveDraw = interpolate(frame, [50, 180], [0, 1], { extrapolateRight: 'clamp' });
  const dotVisible = frame > 80;

  // Params
  const paramStart = 100;
  const paramInterval = 40;

  // Formula
  const formulaStart = paramStart + p.waveParams.length * paramInterval + 10;
  const formulaOpacity = interpolate(frame, [formulaStart, formulaStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const formulaScale = spring({ frame: frame - formulaStart, fps, from: 0.9, to: 1, config: { damping: 14, stiffness: 100 } });

  // Explanation
  const expStart = formulaStart + 40;

  // Amplitude marker animation
  const ampPulse = interpolate(Math.sin(frame / 15), [-1, 1], [0.85, 1.15]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a1a 0%, #0f1729 50%, #111133 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Background grid subtle */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(129,140,248,0.04) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      {/* Title section */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 48,
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -20}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div
            style={{
              padding: '4px 14px',
              background: `${accent}18`,
              border: `1px solid ${accent}40`,
              borderRadius: 8,
              fontSize: 26,
              color: accent,
              fontWeight: 600,
            }}
          >
            {p.subject}
          </div>
        </div>
        <h1 style={{ fontSize: 42, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>{p.topic}</h1>
        <p style={{ fontSize: 30, color: 'rgba(255,255,255,0.35)', marginTop: 4, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== MAIN WAVE VISUALIZATION ========== */}
      <div
        style={{
          position: 'absolute',
          top: 270,
          left: 48,
          width: waveW + 60,
          height: waveH + 60,
        }}
      >
        <svg width={waveW + 60} height={waveH + 60} viewBox={`-30 -30 ${waveW + 60} ${waveH + 60}`}>
          {/* Grid lines */}
          {Array.from({ length: 9 }).map((_, i) => {
            const y = (i / 8) * waveH;
            const opacity = gridDraw * (i === 4 ? 0.3 : 0.08);
            return <line key={`h${i}`} x1={0} y1={y} x2={waveW * gridDraw} y2={y} stroke="white" strokeWidth={i === 4 ? 2 : 1} opacity={opacity} />;
          })}
          {Array.from({ length: 13 }).map((_, i) => {
            const x = (i / 12) * waveW;
            const opacity = gridDraw * 0.08;
            return <line key={`v${i}`} x1={x} y1={0} x2={x} y2={waveH * gridDraw} stroke="white" strokeWidth={1} opacity={opacity} />;
          })}

          {/* Axis labels */}
          <text x={waveW + 10} y={waveH / 2 + 10} fill="rgba(255,255,255,0.4)" fontSize="35" opacity={gridDraw}>t</text>
          <text x={-20} y={2} fill="rgba(255,255,255,0.4)" fontSize="35" opacity={gridDraw}>y</text>

          {/* Primary wave (sine) */}
          <AnimatedWave
            frame={frame}
            width={waveW}
            height={waveH}
            amplitude={amplitude}
            color="#818cf8"
            speed={1}
            phase={0}
            drawProgress={waveDraw}
          />

          {/* Secondary wave (cosine, lighter) */}
          <AnimatedWave
            frame={frame}
            width={waveW}
            height={waveH}
            amplitude={amplitude * 0.6}
            color="#f472b6"
            speed={1}
            phase={Math.PI / 2}
            drawProgress={waveDraw}
          />

          {/* Wave glow */}
          <AnimatedWave
            frame={frame}
            width={waveW}
            height={waveH}
            amplitude={amplitude}
            color="#818cf840"
            speed={1}
            phase={0}
            drawProgress={waveDraw}
          />

          {/* Moving dot on curve */}
          {dotVisible && (
            <MovingDot frame={frame} width={waveW} height={waveH} amplitude={amplitude} speed={1} color="#818cf8" />
          )}

          {/* Amplitude marker arrows */}
          {waveDraw > 0.3 && (
            <g opacity={interpolate(frame, [80, 100], [0, 1], { extrapolateRight: 'clamp' })}>
              {/* A arrow up */}
              <line
                x1={waveW * 0.125}
                y1={waveH / 2}
                x2={waveW * 0.125}
                y2={waveH / 2 - amplitude}
                stroke="#f472b6"
                strokeWidth="2"
                strokeDasharray="6 3"
                opacity="0.7"
              />
              <text
                x={waveW * 0.125 + 12}
                y={waveH / 2 - amplitude / 2}
                fill="#f472b6"
                fontSize="30"
                fontWeight="bold"
                fontStyle="italic"
                transform={`scale(${ampPulse})`}
                style={{ transformOrigin: `${waveW * 0.125 + 12}px ${waveH / 2 - amplitude / 2}px` } as any}
              >
                A
              </text>

              {/* Wavelength marker */}
              <line
                x1={0}
                y1={waveH / 2 + amplitude + 30}
                x2={waveW * 0.5}
                y2={waveH / 2 + amplitude + 30}
                stroke="#fbbf24"
                strokeWidth="2"
                markerEnd="url(#arrowEnd)"
                markerStart="url(#arrowStart)"
                opacity="0.6"
              />
              <text
                x={waveW * 0.25}
                y={waveH / 2 + amplitude + 57}
                fill="#fbbf24"
                fontSize="30"
                fontWeight="bold"
                fontStyle="italic"
                textAnchor="middle"
              >
                λ
              </text>

              <defs>
                <marker id="arrowEnd" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#fbbf24" opacity="0.6" />
                </marker>
                <marker id="arrowStart" markerWidth="8" markerHeight="6" refX="1" refY="3" orient="auto">
                  <polygon points="8 0, 0 3, 8 6" fill="#fbbf24" opacity="0.6" />
                </marker>
              </defs>
            </g>
          )}
        </svg>

        {/* Oscillator preview (vertical spring animation) */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: -80,
            width: 60,
            height: waveH,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: 25, color: 'rgba(255,255,255,0.3)', marginBottom: 8 }}>振子</div>
          <div
            style={{
              width: 2,
              height: waveH * 0.7,
              background: 'rgba(255,255,255,0.1)',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: `${50 - 35 * Math.sin(frame * 0.05)}%`,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: '#818cf8',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 15px rgba(129,140,248,0.5)',
              }}
            />
          </div>
        </div>
      </div>

      {/* ========== RIGHT PANEL: Parameters + Formula + Explanation ========== */}
      <div
        style={{
          position: 'absolute',
          top: 270,
          right: 48,
          width: 420,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Wave parameters */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', fontWeight: 600 }}>
          波动参数
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {p.waveParams.map((param, i) => {
            const start = paramStart + i * paramInterval;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, stiffness: 140, mass: 0.5 },
            });

            return (
              <div
                key={i}
                style={{
                  padding: '16px',
                  background: `${param.color}0a`,
                  borderRadius: 14,
                  border: `1px solid ${param.color}25`,
                  opacity: progress,
                  transform: `scale(${0.9 + progress * 0.1})`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <span
                    style={{
                      fontSize: 34,
                      fontWeight: 800,
                      fontFamily: '"Times New Roman", Georgia, serif',
                      fontStyle: 'italic',
                      color: param.color,
                    }}
                  >
                    {param.symbol}
                  </span>
                  <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>{param.label}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{param.value}</div>
              </div>
            );
          })}
        </div>

        {/* Formula */}
        <div
          style={{
            padding: '24px',
            background: `${accent}0c`,
            borderRadius: 18,
            border: `2px solid ${accent}30`,
            textAlign: 'center',
            opacity: formulaOpacity,
            transform: `scale(${formulaScale})`,
            marginTop: 20,
          }}
        >
          <div style={{ fontSize: 26, color: `${accent}80`, marginBottom: 10, letterSpacing: '0.1em' }}>核心公式</div>
          <div
            style={{
              fontSize: 40,
              fontFamily: '"Times New Roman", Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 700,
              color: 'white',
              textShadow: `0 0 30px ${accent}40`,
            }}
          >
            {p.formula}
          </div>
        </div>

        {/* Explanation lines */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          {p.explanation.map((line, i) => {
            const start = expStart + i * 30;
            const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
            const x = interpolate(frame, [start, start + 20], [20, 0], { extrapolateRight: 'clamp' });

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity,
                  transform: `translateX(${x}px)`,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: accent,
                    flexShrink: 0,
                    opacity: 0.6,
                  }}
                />
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{line}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: `linear-gradient(90deg, ${accent}, #f472b6)`,
        }}
      />
    </AbsoluteFill>
  );
};
